import { SourceError } from "../core/errors.js";

export interface HeliusTransaction {
  signature: string;
  timestamp: number;
  fee: number;
  programs: string[];
  type: string;
}

export interface HeliusSourceConfig {
  apiKey: string;
  rpcUrl?: string;
  timeout: number;
  maxRetries: number;
}

/**
 * Helius adapter. Fetches wallet transaction history via the enhanced
 * transactions endpoint, normalizes it into `HeliusTransaction` records,
 * and exposes it to detectors.
 */
export class HeliusSource {
  private readonly baseUrl: string;
  private readonly apiKey: string;
  private readonly timeout: number;
  private readonly maxRetries: number;

  constructor(config: HeliusSourceConfig) {
    this.apiKey = config.apiKey;
    this.baseUrl = config.rpcUrl ?? "https://api.helius.xyz/v0";
    this.timeout = config.timeout;
    this.maxRetries = config.maxRetries;
  }

  async getWalletHistory(
    wallet: string,
    opts: { limit?: number; before?: string } = {}
  ): Promise<HeliusTransaction[]> {
    const limit = opts.limit ?? 100;
    const url = new URL(`${this.baseUrl}/addresses/${wallet}/transactions`);
    url.searchParams.set("api-key", this.apiKey);
    url.searchParams.set("limit", String(Math.min(limit, 100)));
    if (opts.before) url.searchParams.set("before", opts.before);

    const raw = await this.fetchWithRetry(url);
    if (!Array.isArray(raw)) {
      throw new SourceError("helius", "unexpected response shape");
    }

    return raw.map((tx: unknown) => {
      const t = tx as Record<string, unknown>;
      return {
        signature: String(t.signature ?? ""),
        timestamp: Number(t.timestamp ?? 0),
        fee: Number(t.fee ?? 0),
        programs: Array.isArray(t.accountData)
          ? extractPrograms(t as HeliusRawTx)
          : [],
        type: String(t.type ?? "UNKNOWN"),
      };
    });
  }

  private async fetchWithRetry(url: URL): Promise<unknown> {
    let lastError: unknown;
    for (let attempt = 0; attempt <= this.maxRetries; attempt++) {
      try {
        const controller = new AbortController();
        const timer = setTimeout(() => controller.abort(), this.timeout);
        const res = await fetch(url, { signal: controller.signal });
        clearTimeout(timer);

        if (res.status === 429) {
          const retryAfter = Number(res.headers.get("retry-after") ?? "2");
          await sleep(retryAfter * 1000);
          continue;
        }
        if (!res.ok) {
          throw new SourceError(
            "helius",
            `HTTP ${res.status}`,
            res.status
          );
        }
        return await res.json();
      } catch (err) {
        lastError = err;
        if (attempt < this.maxRetries) {
          await sleep(500 * (attempt + 1));
        }
      }
    }
    throw lastError instanceof Error
      ? new SourceError("helius", lastError.message)
      : new SourceError("helius", "unknown error");
  }
}

interface HeliusRawTx {
  accountData?: Array<{ account?: string; nativeBalanceChange?: number }>;
  instructions?: Array<{ programId?: string }>;
}

function extractPrograms(tx: HeliusRawTx): string[] {
  const programs = new Set<string>();
  for (const ix of tx.instructions ?? []) {
    if (ix.programId) programs.add(ix.programId);
  }
  return Array.from(programs);
}

function sleep(ms: number): Promise<void> {
  return new Promise((r) => setTimeout(r, ms));
}
